import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Pencil, X, Check, Loader as Loader2, ShieldCheck, User, Plus, Trash2 } from 'lucide-react';

interface Reseller {
  id: string;
  name: string;
}

interface UserRow {
  id: string;
  email: string;
  role: 'admin' | 'reseller';
  reseller_id: string | null;
  reseller_name: string | null;
}

interface EditState {
  role: 'admin' | 'reseller';
  reseller_id: string;
}

interface NewUserState {
  email: string;
  password: string;
  role: 'admin' | 'reseller';
  reseller_id: string;
}

async function callAdminUsers(method: string, path = '', body?: object) {
  const { data: { session } } = await supabase.auth.getSession();
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-users${path}`;
  const res = await fetch(url, {
    method,
    headers: {
      'Authorization': `Bearer ${session?.access_token}`,
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return res.json();
}

export function UsersTab() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState>({ role: 'reseller', reseller_id: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [newUser, setNewUser] = useState<NewUserState>({ email: '', password: '', role: 'reseller', reseller_id: '' });
  const [creating, setCreating] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    const [usersRes, resellersRes] = await Promise.all([
      callAdminUsers('GET'),
      supabase.from('resellers').select('id, name').order('name'),
    ]);

    const resellerList: Reseller[] = resellersRes.data ?? [];
    setResellers(resellerList);

    if (usersRes.error) {
      setError(usersRes.error);
      setLoading(false);
      return;
    }

    const resellerMap = Object.fromEntries(resellerList.map(r => [r.id, r.name]));
    const rows: UserRow[] = (usersRes.users ?? []).map((u: any) => ({
      id: u.id,
      email: u.email,
      role: u.role,
      reseller_id: u.reseller_id,
      reseller_name: u.reseller_id ? resellerMap[u.reseller_id] ?? null : null,
    }));
    setUsers(rows);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const startEdit = (u: UserRow) => {
    setEditingId(u.id);
    setEditState({ role: u.role, reseller_id: u.reseller_id ?? '' });
    setError(null);
  };

  const cancelEdit = () => { setEditingId(null); setError(null); };

  const saveEdit = async (id: string) => {
    setSaving(true);
    setError(null);
    const { error } = await supabase
      .from('profiles')
      .update({
        role: editState.role,
        reseller_id: editState.reseller_id || null,
      })
      .eq('id', id);
    setSaving(false);
    if (error) { setError(error.message); return; }
    setEditingId(null);
    fetchData();
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.password) {
      setError('Email and password are required.');
      return;
    }
    setCreating(true);
    setError(null);
    const res = await callAdminUsers('POST', '', {
      email: newUser.email,
      password: newUser.password,
      role: newUser.role,
      reseller_id: newUser.reseller_id || null,
    });
    setCreating(false);
    if (res.error) { setError(res.error); return; }
    setShowNew(false);
    setNewUser({ email: '', password: '', role: 'reseller', reseller_id: '' });
    fetchData();
  };

  const deleteUser = async (id: string, email: string) => {
    if (!confirm(`Delete user ${email}? This cannot be undone.`)) return;
    setError(null);
    const res = await callAdminUsers('DELETE', `/${id}`);
    if (res.error) { setError(res.error); return; }
    fetchData();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">Users</h2>
        <Button
          onClick={() => { setShowNew(true); setError(null); }}
          className="bg-[#212b4f] hover:bg-[#2d3a6b] text-white h-8 text-sm gap-1.5"
        >
          <Plus className="h-3.5 w-3.5" />
          New User
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">{error}</div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {showNew && (
          <div className="border-b border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-medium text-slate-700 mb-3">New User</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Email</Label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={e => setNewUser(s => ({ ...s, email: e.target.value }))}
                  className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
                  placeholder="user@company.com"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Password</Label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={e => setNewUser(s => ({ ...s, password: e.target.value }))}
                  className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
                  placeholder="••••••••"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Role</Label>
                <select
                  value={newUser.role}
                  onChange={e => setNewUser(s => ({ ...s, role: e.target.value as 'admin' | 'reseller' }))}
                  className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
                >
                  <option value="reseller">Reseller</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Reseller</Label>
                <select
                  value={newUser.reseller_id}
                  onChange={e => setNewUser(s => ({ ...s, reseller_id: e.target.value }))}
                  className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
                  disabled={newUser.role === 'admin'}
                >
                  <option value="">— None —</option>
                  {resellers.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button
                onClick={createUser}
                disabled={creating || !newUser.email || !newUser.password}
                className="bg-[#212b4f] hover:bg-[#2d3a6b] text-white h-8 text-sm gap-1.5"
              >
                {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Create
              </Button>
              <Button
                variant="ghost"
                onClick={() => { setShowNew(false); setError(null); }}
                className="h-8 text-sm text-slate-600"
              >
                <X className="h-3.5 w-3.5 mr-1" />
                Cancel
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-slate-400 text-sm">No users found</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Email</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Role</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Reseller</th>
                <th className="px-4 py-3 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map(u => (
                <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                  {editingId === u.id ? (
                    <>
                      <td className="px-4 py-3 text-slate-600 text-sm">{u.email}</td>
                      <td className="px-4 py-2">
                        <select
                          value={editState.role}
                          onChange={e => setEditState(s => ({ ...s, role: e.target.value as 'admin' | 'reseller' }))}
                          className="px-2 py-1 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
                        >
                          <option value="admin">Admin</option>
                          <option value="reseller">Reseller</option>
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <select
                          value={editState.reseller_id}
                          onChange={e => setEditState(s => ({ ...s, reseller_id: e.target.value }))}
                          className="px-2 py-1 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
                          disabled={editState.role === 'admin'}
                        >
                          <option value="">— None —</option>
                          {resellers.map(r => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex gap-1 justify-end">
                          <button
                            onClick={() => saveEdit(u.id)}
                            disabled={saving}
                            className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 transition-colors"
                          >
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3 text-slate-800">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          u.role === 'admin'
                            ? 'bg-[#212b4f]/10 text-[#212b4f]'
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {u.role === 'admin' ? <ShieldCheck className="h-3 w-3" /> : <User className="h-3 w-3" />}
                          {u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500">{u.reseller_name ?? <span className="text-slate-300">—</span>}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-end">
                          <button
                            onClick={() => startEdit(u)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-[#212b4f] hover:bg-slate-100 transition-colors"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => deleteUser(u.id, u.email)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
